import type {ParsedSteamInput, SteamID64} from '../types/steam';
import {SteamID64 as toSteamID64} from '../types/steam';

const STEAMID64_BASE: bigint = 76561197960265728n;

export function parseSteamInput(raw: string): ParsedSteamInput {
    const str: string = (raw || '').trim();
    if (!str) throw new Error('Please enter a Steam profile name, URL, or ID.');

    // URL form
    try {
        const u: URL = new URL(str);
        if (u.hostname.includes('steamcommunity.com')) {
            const parts: string[] = u.pathname.split('/').filter(Boolean);
            const kind: string = parts[0];
            const value: string = parts[1];
            if (kind === 'profiles') {
                // Only accept legit SteamID64: 17 digits starting with 76
                if (/^76\d{15}$/.test(value)) {
                    return {type: 'steamid64', value};
                }
                throw new Error('Invalid Steam profiles URL. Expected a valid 17-digit SteamID64.');
            }
            if (kind === 'id' && value) {
                return {type: 'vanity', value};
            }
        }
    } catch {
        // Not a URL, continue
    }

    // SteamID64
    if (/^76\d{15}$/.test(str)) return {type: 'steamid64', value: str};

    // SteamID32 (e.g., 123456)
    if (/^\d+$/.test(str)) {
        // Treat as SteamID32 only if within unsigned 32-bit range (0..4294967295)
        try {
            const n: bigint = BigInt(str);
            if (n >= 0n && n <= 4294967295n) {
                return {type: 'steamid32', value: str};
            }
        } catch {
            // fall through to vanity
        }
    }

    // Old SteamID format (e.g., STEAM_0:1:123456 or STEAM_1:0:123456)
    const oldMatch: RegExpMatchArray | null = str.match(/^STEAM_([01]):([01]):(\d+)$/i);
    if (oldMatch) return {type: 'steamid', value: str};

    // SteamID3 format (e.g., [U:1:123456])
    const sid3Match: RegExpMatchArray | null = str.match(/^\[U:1:(\d+)\]$/);
    if (sid3Match) return {type: 'steamid3', value: str};

    // Assume vanity name
    return {type: 'vanity', value: str};
}

export function convertToSteamId64(parsed: ParsedSteamInput): SteamID64 {
    switch (parsed.type) {
        case 'steamid64':
            return toSteamID64(parsed.value);
        case 'steamid32': {
            const id32: bigint = BigInt(parsed.value);
            return toSteamID64((STEAMID64_BASE + id32).toString());
        }
        case 'steamid': {
            const match: RegExpMatchArray | null = parsed.value.match(/^STEAM_([01]):([01]):(\d+)$/i);
            if (!match) throw new Error('Invalid SteamID format');
            const [, , y, z] = match;
            const id32: bigint = BigInt(z) * 2n + BigInt(y);
            return toSteamID64((STEAMID64_BASE + id32).toString());
        }
        case 'steamid3': {
            const match: RegExpMatchArray | null = parsed.value.match(/^\[U:1:(\d+)\]$/);
            if (!match) throw new Error('Invalid SteamID3 format');
            const id32: bigint = BigInt(match[1]);
            return toSteamID64((STEAMID64_BASE + id32).toString());
        }
        default:
            throw new Error('Cannot convert vanity name directly. Must resolve first.');
    }
}
